import { FC, useState } from 'react';
import { useBlocker } from '@tanstack/react-router';
import { formOptions } from '@tanstack/react-form';
import { toast } from 'sonner';

import { useAppForm } from '@new/common/hooks/form';
import { Button } from '@new/common/ui/Button/Button';
import { ConfirmationModal } from '@new/common/ui/ConfirmationModal/ConfirmationModal';
import { getErrorMessage } from '@new/lib/getErrorMessage';

import { useEvaluation } from '../../hooks/useEvaluation';
import { WizardStepper, type WizardStepMeta } from './WizardStepper';
import { PersonalInfoStep } from './steps/PersonalInfoStep';
import { AcademicSituationStep } from './steps/AcademicSituationStep';
import { CreditsInfoStep } from './steps/CreditsInfoStep';
import { SummaryStep } from './steps/SummaryStep';
import {
  STEP_FIELDS,
  wizardDefaultValues,
  wizardSchema,
  type WizardValues,
} from '../../schemas/wizard.schema';
import type { Evaluation } from '@new/features/transcripts/shared/types';

const STEPS: WizardStepMeta[] = [
  { id: '01', name: 'Personal Information', description: 'Name & name change' },
  { id: '02', name: 'Academic Situation', description: 'Your prior education' },
  { id: '03', name: 'Credits Information', description: 'Program & credits' },
  { id: '04', name: 'Summary', description: 'Review & finish' },
];

interface InformationWizardProps {
  evaluation: Evaluation | null;
  onDone: () => void;
  onCancel?: () => void;
}

const toDefaults = (e: Evaluation | null): WizardValues => {
  if (!e) return wizardDefaultValues;
  return {
    hasNameChange: !!e.formerName,
    formerName: e.formerName,
    nameChangeReason: e.nameChangeReason,
    nameChangeDocumentId: e.nameChangeDocumentId,
    priorEducationStatus: e.priorEducationStatus ?? 'no_credits',
    targetDegreeType: e.targetDegreeType ?? 'bachelor',
    targetDegreeId: e.targetDegreeId,
    estimatedCredits: e.estimatedCredits,
    creditSourceCount: e.creditSourceCount,
    degreeName: e.degreeName,
    degreeInstitution: e.degreeInstitution,
    additionalCredits: e.additionalCredits,
    additionalCreditsSource: e.additionalCreditsSource,
  };
};

export const InformationWizard: FC<InformationWizardProps> = ({ evaluation, onDone, onCancel }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [finished, setFinished] = useState(false);
  const { updatePersonalInfo, updateAcademicInfo } = useEvaluation();

  const form = useAppForm({
    ...formOptions({ defaultValues: toDefaults(evaluation) }),
    validators: {
      onChange: ({ value }: { value: WizardValues }) => {
        const parsed = wizardSchema.safeParse(value);
        if (parsed.success) return { fields: {} };
        const errors: Record<string, string> = {};
        parsed.error.issues.forEach((issue) => {
          const field = issue.path[0];
          if (typeof field === 'string' && !errors[field]) errors[field] = issue.message;
        });
        return { fields: errors };
      },
    },
    onSubmit: async ({ value }: { value: WizardValues }) => {
      const parsed = wizardSchema.safeParse(value);
      if (!parsed.success) return;
      const v = parsed.data;
      try {
        await updatePersonalInfo({
          formerName: v.hasNameChange ? (v.formerName ?? null) : null,
          nameChangeReason: v.hasNameChange ? (v.nameChangeReason ?? null) : null,
          nameChangeDocumentId: v.hasNameChange ? (v.nameChangeDocumentId ?? null) : null,
        });
        await updateAcademicInfo({
          priorEducationStatus: v.priorEducationStatus,
          targetDegreeType: v.targetDegreeType ?? null,
          targetDegreeId: v.targetDegreeId ?? null,
          estimatedCredits: v.estimatedCredits ?? null,
          creditSourceCount: v.creditSourceCount ?? null,
          degreeName: v.degreeName ?? null,
          degreeInstitution: v.degreeInstitution ?? null,
          additionalCredits: v.additionalCredits ?? null,
          additionalCreditsSource: v.additionalCreditsSource ?? null,
        });
        setFinished(true);
        onDone();
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    },
  });

  const { proceed, reset, status } = useBlocker({
    shouldBlockFn: () => !finished && form.state.isDirty,
    withResolver: true,
  });

  const isLastStep = stepIndex === STEPS.length - 1;

  const stepHasErrors = (): boolean => {
    const fieldErrors = form.state.fieldMeta;
    return STEP_FIELDS[stepIndex].some((name) => (fieldErrors?.[name]?.errors?.length ?? 0) > 0);
  };

  const goNext = async () => {
    const stepFields = STEP_FIELDS[stepIndex];
    await Promise.all(stepFields.map((name) => form.validateField(name, 'change')));
    stepFields.forEach((name) => form.setFieldMeta(name, (meta) => ({ ...meta, isTouched: true })));
    if (stepHasErrors()) return;
    setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
  };

  const goBack = () => setStepIndex((i) => Math.max(i - 1, 0));

  const renderStep = () => {
    switch (stepIndex) {
      case 0:
        return <PersonalInfoStep form={form} />;
      case 1:
        return <AcademicSituationStep form={form} />;
      case 2:
        return <CreditsInfoStep form={form} />;
      default:
        return <SummaryStep values={form.state.values as WizardValues} />;
    }
  };

  return (
    <div className="flex flex-col gap-8">
      <WizardStepper steps={STEPS} currentIndex={stepIndex} onStepClick={setStepIndex} />

      <div className="flex min-h-[420px] w-full flex-col items-stretch gap-12 self-stretch rounded-[9px] border border-solid border-[#CACDCE] bg-(--neutral-white-NW1000) p-8 [&_[data-slot=select]]:h-[48px]! [&_[data-slot=select]]:rounded-[8px]! [&_[data-slot=select]]:border-[1.5px]! [&_[data-slot=select]]:border-solid! [&_[data-slot=select]]:border-[#E1E7EA]! [&_[data-slot=select]]:bg-[#FCFDFD]! [&_input[data-slot=input]]:h-[48px]! [&_input[data-slot=input]]:md:h-[48px]! [&_input[data-slot=input]]:xl:h-[48px]! [&_input[data-slot=input]]:rounded-[8px]! [&_input[data-slot=input]]:border-[1.5px]! [&_input[data-slot=input]]:border-solid! [&_input[data-slot=input]]:border-[#E1E7EA]! [&_input[data-slot=input]]:bg-[#FCFDFD]!">
        {renderStep()}
      </div>

      <div className="flex w-full items-center justify-between">
        <div className="min-w-[120px]">
          {stepIndex > 0 ? (
            <Button variant="neutral" onClick={goBack} text="Back" className="w-[120px]" />
          ) : onCancel ? (
            <Button variant="neutral" onClick={onCancel} text="Cancel" className="w-[120px]" />
          ) : null}
        </div>
        <div className="min-w-[120px]">
          {isLastStep ? (
            <Button
              variant="primary"
              onClick={() => form.handleSubmit()}
              disabled={form.state.isSubmitting}
              text={form.state.isSubmitting ? 'Finishing…' : 'Finish'}
              className="w-[120px]"
            />
          ) : (
            <Button variant="primary" onClick={goNext} text="Next" className="w-[120px]" />
          )}
        </div>
      </div>

      <ConfirmationModal
        isOpen={status === 'blocked'}
        title="Leave without saving?"
        description="Your information has not been saved yet and will be lost if you leave."
        confirmText="Leave"
        cancelText="Stay"
        onConfirm={() => proceed?.()}
        onClose={() => reset?.()}
      />
    </div>
  );
};
